import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface TradingKillSwitches {
  TRADING_ENABLED: boolean;
  ENTRY_ENABLED: boolean;
  EXIT_ENABLED: boolean;
  NEW_SIGNALS_ENABLED: boolean;
  MAX_GLOBAL_CONCURRENCY: number;
  EXIT_FALLBACK_ENABLED: boolean;  // Phase 0.3
  ENTRY_PIPELINE_ENABLED: boolean;  // Phase 2
  EXIT_PIPELINE_ENABLED: boolean;  // Phase 2
  POSITION_MONITOR_CAN_SEND_ORDERS: boolean;  // Phase 2
}

export interface UpdateKillSwitchRequest {
  value: boolean | number;
}

export interface DailyCircuitBreakerStatus {
  halted: boolean;
  reason: string | null;
  date: string | null;
  daily_pnl: number;
  equity: number | null;
  pnl_pct: number | null;
  trailing: {
    enabled: boolean;
    armed: boolean;
    arm_pct: number | null;
    drawdown_pct: number | null;
    peak_pnl_pct: number | null;
    halt_at_pnl_pct: number | null;
    pnl_pct: number | null;
  } | null;
}

export async function getDailyCircuitBreakerStatus(): Promise<DailyCircuitBreakerStatus> {
  try {
    const response = await apiClient.get<DailyCircuitBreakerStatus>(
      "/admin/system-settings/trading/daily-circuit-breaker/status",
    );
    return response.data;
  } catch (error) {
    logger.error("Error fetching daily circuit breaker status:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function getTradingKillSwitches(): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.get<TradingKillSwitches>(
      "/admin/system-settings/trading",
    );
    return response.data;
  } catch (error) {
    logger.error("Error fetching trading kill switches:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateTradingEnabled(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/trading-enabled",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating TRADING_ENABLED:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateEntryEnabled(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/entry-enabled",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating ENTRY_ENABLED:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateExitEnabled(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/exit-enabled",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating EXIT_ENABLED:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateNewSignalsEnabled(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/new-signals-enabled",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating NEW_SIGNALS_ENABLED:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateMaxGlobalConcurrency(
  concurrency: number,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/max-global-concurrency",
      { value: concurrency },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating MAX_GLOBAL_CONCURRENCY:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateExitFallbackEnabled(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/exit-fallback-enabled",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating EXIT_FALLBACK_ENABLED:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updatePositionMonitorCanSendOrders(
  enabled: boolean,
): Promise<TradingKillSwitches> {
  try {
    const response = await apiClient.patch<TradingKillSwitches>(
      "/admin/system-settings/trading/position-monitor-can-send-orders",
      { value: enabled },
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating POSITION_MONITOR_CAN_SEND_ORDERS:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

